import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { GroupEntity, GroupMemberEntity, GroupMemberTotalsEntity } from '@interpals/api-web';

import {
    FETCH_GROUPS_MEMBERS_LIMIT,
    clearGroup,
    editGroup,
    fetchGroup,
    fetchGroupMembers,
    groupSubscribe,
    groupUnsubscribe,
    setGroupMemberType,
    clearGroupMembers,
    FetchGroupMembersParams,
} from 'redux/group/actions';

export interface GroupMembersState {
    all: GroupMemberEntity[];
    admin: GroupMemberEntity[];
    blocked: GroupMemberEntity[];

    allHasMore: boolean;
    adminHasMore: boolean;
    blockedHasMore: boolean;

    totals: GroupMemberTotalsEntity;
}

export interface GroupState {
    group: GroupEntity | null;
    members: GroupMembersState;
}

export const initialState: GroupState = {
    group: null,
    members: {
        all: [],
        admin: [],
        blocked: [],

        allHasMore: true,
        adminHasMore: true,
        blockedHasMore: true,

        totals: {
            all: 0,
            admin: 0,
            blocked: 0,
        },
    },
};

const groupSlice = createSlice({
    name: 'group',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchGroup.fulfilled, (state: GroupState, { payload }) => {
            state.group = payload;
            state.members.totals.all = payload.members_count;
        });

        builder.addCase(editGroup.fulfilled, (state: GroupState, { payload }) => {
            state.group = payload;
        });

        builder.addCase(fetchGroupMembers.fulfilled, (state: GroupState, { payload }) => {
            const hasMore = payload.response.data.length === FETCH_GROUPS_MEMBERS_LIMIT;
            switch (payload.member_type) {
                case 'blocked':
                    state.members.blocked = [...state.members.blocked, ...payload.response.data];
                    state.members.blockedHasMore = hasMore;
                    break;

                case 'admin':
                    state.members.admin = [...state.members.admin, ...payload.response.data];
                    state.members.adminHasMore = hasMore;
                    break;

                default:
                    state.members.all = [...state.members.all, ...payload.response.data];
                    state.members.allHasMore = hasMore;
                    break;
            }

            if (payload.response.totals) {
                state.members.totals = payload.response.totals;
            }
        });

        builder.addCase(fetchGroupMembers.pending, (state: GroupState, action) => {
            const member_type = (action.meta.arg as FetchGroupMembersParams).member_type;

            // Prevent requests from executing concurrently. This occurs for example when opening the members
            // modal by URL, since we load group members for the "Following" component at the same time as
            // members for the members modal
            //
            // These values will be reset to true (if more are available) once 'fulfilled' fires.
            switch (member_type) {
                case 'blocked':
                    state.members.blockedHasMore = false;
                    break;

                case 'admin':
                    state.members.adminHasMore = false;
                    break;

                default:
                    state.members.allHasMore = false;
                    break;
            }
        });

        builder.addCase(setGroupMemberType.fulfilled, (state: GroupState, { payload }) => {
            const member = [...state.members.all, ...state.members.admin, ...state.members.blocked].filter(
                (item) => item.uid === payload.uid,
            )[0];

            const otherMembers = (arr: GroupMemberEntity[], uid: string) => arr.filter((item) => item.uid !== uid);

            switch (payload.member_type) {
                case 'blocked':
                    // Remove from all & admin
                    state.members.all = otherMembers(state.members.all, payload.uid);
                    state.members.admin = otherMembers(state.members.admin, payload.uid);
                    state.members.totals.all--;
                    state.members.totals.blocked++;
                    if (member.member_type === 'owner' || member.member_type === 'moderator') {
                        state.members.totals.admin--;
                    }

                    // Add to blocked
                    state.members.blocked = [
                        {
                            ...member,
                            member_type: payload.member_type,
                        },
                        ...otherMembers(state.members.blocked, payload.uid),
                    ];
                    break;

                case 'moderator':
                case 'owner':
                    state.members.blocked = otherMembers(state.members.blocked, payload.uid);
                    state.members.admin = [
                        {
                            ...member,
                            member_type: payload.member_type,
                        },
                        ...otherMembers(state.members.admin, payload.uid),
                    ];
                    state.members.all = [
                        {
                            ...member,
                            member_type: payload.member_type,
                        },
                        ...otherMembers(state.members.all, payload.uid),
                    ];

                    if (member.member_type === 'standard') {
                        state.members.totals.admin++;
                    }
                    break;

                default:
                    state.members.blocked = otherMembers(state.members.blocked, payload.uid);
                    state.members.admin = otherMembers(state.members.admin, payload.uid);
                    state.members.all = [
                        {
                            ...member,
                            member_type: payload.member_type,
                        },
                        ...otherMembers(state.members.all, payload.uid),
                    ];

                    if (member.member_type === 'owner' || member.member_type === 'moderator') {
                        state.members.totals.admin--;
                    }
                    if (member.member_type === 'blocked') {
                        state.members.totals.blocked--;
                    }
            }

            // Group entity member type refers to current user only
            if (
                state.group &&
                payload.isUserSelf &&
                state.group.id === payload.groupId &&
                payload.member_type !== 'blocked'
            ) {
                state.group.user_member_type = payload.member_type;
            }
        });

        builder.addCase(clearGroupMembers, (state: GroupState) => {
            state.members = initialState.members;
        });

        builder.addMatcher(
            isAnyOf(groupSubscribe.fulfilled, groupUnsubscribe.fulfilled),
            (state: GroupState, { payload }) => {
                state.group = payload;
            },
        );

        builder.addMatcher(isAnyOf(clearGroup, fetchGroup.rejected, fetchGroupMembers.rejected), () => initialState);
    },
});

export default groupSlice.reducer;
